/**
 * Drilling in Progress hole list + PDF page assembly for daily reports.
 */
import { looksLikeEmail, normalizeEmailKey } from '../../services/graphUserService';
import { buildZoneColorMap, buildAllZoneFilters, buildMapHolesForReport } from './drillingMapForReport';
import { captureHoleInsetMapSnapshot } from './captureDrillHoleMap';
import { HOLE_INSET_MAP } from './templateConfig';

function isPresent(val) {
  if (val == null || val === '') return false;
  const text = String(val).trim();
  if (!text) return false;
  if (text.toLowerCase() === 'nan') return false;
  return true;
}

function firstPresent(...vals) {
  for (const v of vals) {
    if (isPresent(v)) return v;
  }
  return null;
}

export function resolveCampaignDrillzId(projectSources, selectedProjectId) {
  if (selectedProjectId == null || selectedProjectId === 'all') return null;
  const project = (projectSources || []).find(
    (src) => String(src.project_id) === String(selectedProjectId)
  );
  const campaignId = project?.drillz_campaign_id;
  if (campaignId == null || String(campaignId).trim() === '') return null;
  return String(campaignId);
}

export function normalizeSelectedInProgressHoles(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => ({
      collar_uuid: entry.collar_uuid != null ? String(entry.collar_uuid) : null,
      hole_id: entry.hole_id != null ? String(entry.hole_id) : null,
    }))
    .filter((entry) => entry.collar_uuid || entry.hole_id);
}

export function selectedInProgressHolesEqual(a, b) {
  const left = normalizeSelectedInProgressHoles(a);
  const right = normalizeSelectedInProgressHoles(b);
  if (left.length !== right.length) return false;
  return left.every(
    (entry, index) =>
      entry.collar_uuid === right[index].collar_uuid &&
      entry.hole_id === right[index].hole_id
  );
}

export function findDashboardRowForHole(dashboardData, holeId) {
  if (!holeId || !Array.isArray(dashboardData)) return null;
  const needle = String(holeId).trim().toLowerCase();
  return (
    dashboardData.find(
      (row) => String(row.hole_id || row.hole_name || '').trim().toLowerCase() === needle
    ) || null
  );
}

export function mergeHoleWithDashboardFallback(drillzHole, dashboardRow) {
  const row = dashboardRow || {};
  const actualFromDashboard = firstPresent(
    row.current_depth,
    row.final_depth,
    row.master_final_depth
  );

  return {
    ...drillzHole,
    collar_uuid: drillzHole?.collar_uuid ?? null,
    hole_id: drillzHole?.hole_id ?? null,
    display_label: drillzHole?.display_label ?? drillzHole?.hole_id ?? '',
    dataset: firstPresent(drillzHole?.dataset, row.dataset),
    purpose: firstPresent(drillzHole?.purpose, row.purpose),
    planned_depth: firstPresent(drillzHole?.planned_depth, row.planned_depth),
    target_description: firstPresent(
      drillzHole?.target_description,
      row.master_target_lens,
      row.target_description
    ),
    actual_depth: firstPresent(drillzHole?.actual_depth, actualFromDashboard),
    easting: firstPresent(drillzHole?.easting, row.trace_collar_easting_26908),
    northing: firstPresent(drillzHole?.northing, row.trace_collar_northing_26908),
    azimuth: firstPresent(drillzHole?.azimuth, row.master_azimuth),
    dip: firstPresent(drillzHole?.dip, row.master_dip),
    rig_number: firstPresent(drillzHole?.rig_number, row.drill_rig),
    geologist: firstPresent(drillzHole?.geologist, row.geologist),
    drilling_started: drillzHole?.drilling_started ?? null,
    latitude: parseHoleCoord(row.latitude),
    longitude: parseHoleCoord(row.longitude),
    prospect: firstPresent(row.prospect, drillzHole?.prospect),
    hole_status: firstPresent(row.hole_status, drillzHole?.hole_status) || 'In Progress',
  };
}

function parseHoleCoord(value) {
  const n = Number(value);
  return Number.isFinite(n) && n !== 0 ? n : null;
}

function resolveHoleFromPool(selectedRef, inProgressHolesList) {
  if (!selectedRef) return null;
  const collarUuid = selectedRef.collar_uuid ? String(selectedRef.collar_uuid) : null;
  const holeId = selectedRef.hole_id ? String(selectedRef.hole_id) : null;

  return (
    (inProgressHolesList || []).find((hole) => {
      if (collarUuid && hole.collar_uuid && String(hole.collar_uuid) === collarUuid) {
        return true;
      }
      if (holeId && hole.hole_id && String(hole.hole_id) === holeId) {
        return true;
      }
      return false;
    }) || null
  );
}

function buildPageSubtitle(hole) {
  const parts = [hole.dataset, hole.purpose].filter(isPresent).map(String);
  return parts.length ? parts.join(' · ') : null;
}

export function buildInProgressHolePages({
  selectedRefs,
  inProgressHolesList,
  dashboardData,
  quicklogByCollar = {},
}) {
  const refs = normalizeSelectedInProgressHoles(selectedRefs);
  const pages = [];

  for (const selectedRef of refs) {
    const drillzHole = resolveHoleFromPool(selectedRef, inProgressHolesList);
    if (!drillzHole) continue;

    const dashboardRow = findDashboardRowForHole(dashboardData, drillzHole.hole_id);
    const hole = mergeHoleWithDashboardFallback(drillzHole, dashboardRow);
    const quicklogKey = String(hole.collar_uuid || hole.hole_id || '');
    const quicklogRows = quicklogByCollar[quicklogKey] || [];

    pages.push({
      ...hole,
      quicklogRows,
      pageTitle: hole.display_label || hole.hole_id || 'Drilling in progress',
      pageSubtitle: buildPageSubtitle(hole),
    });
  }

  return pages;
}

/**
 * Apply Graph-resolved display names to hole pages (keys: normalized email).
 */
export function applyGeologistDisplayNames(pages, nameMap) {
  if (!Array.isArray(pages) || !nameMap?.size) return pages;

  return pages.map((page) => {
    const raw = page?.geologist;
    if (!raw || !looksLikeEmail(raw)) return page;

    const resolved = nameMap.get(normalizeEmailKey(raw));
    if (!resolved || resolved === raw) return page;

    return { ...page, geologist: resolved };
  });
}

/**
 * Capture ~300 m inset maps for hole PDF pages (requires geolocated dashboard rows).
 */
export async function attachHoleInsetMapSnapshots(
  pages,
  container,
  { dashboardData, selectedProjectId, onProgress }
) {
  if (!Array.isArray(pages) || !pages.length || !container) return pages;

  const campaignHoles = buildMapHolesForReport(dashboardData, selectedProjectId);
  const zoneColorMap = buildZoneColorMap(campaignHoles);
  const zoneFilters = buildAllZoneFilters(zoneColorMap);
  const updated = [];

  for (let index = 0; index < pages.length; index += 1) {
    const page = pages[index];
    const lat = parseHoleCoord(page.latitude);
    const lon = parseHoleCoord(page.longitude);

    if (onProgress) {
      onProgress(`Capturing location map ${index + 1}/${pages.length}...`);
    }

    if (lat == null || lon == null) {
      updated.push(page);
      continue;
    }

    const holeForMap = {
      hole_id: page.hole_id,
      latitude: lat,
      longitude: lon,
      hole_status: page.hole_status || 'In Progress',
      prospect: page.prospect || 'Unknown',
      drill_rig: page.rig_number,
    };

    try {
      const insetMapDataUrl = await captureHoleInsetMapSnapshot(container, {
        hole: holeForMap,
        campaignHoles,
        zoneColorMap,
        zoneFilters,
        spanM: HOLE_INSET_MAP.spanM,
      });
      updated.push({
        ...page,
        insetMapDataUrl: insetMapDataUrl || null,
      });
    } catch (error) {
      console.warn('Hole inset map capture failed:', page.hole_id, error);
      updated.push(page);
    }
  }

  return updated;
}

export function formatReportDepth(val) {
  if (!isPresent(val)) return '—';
  const n = Number(val);
  if (Number.isFinite(n)) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
  return String(val);
}

export function formatReportCoord(val) {
  if (!isPresent(val)) return '—';
  const n = Number(val);
  if (Number.isFinite(n)) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  }
  return String(val);
}

export function formatReportAngle(val) {
  if (!isPresent(val)) return '—';
  const n = Number(val);
  if (Number.isFinite(n)) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
  return String(val);
}
